// src/context/Notifications.jsx
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLogin } from "./logIncontext";
import { useTranslation } from "react-i18next";

import { getNotificationsWsUrl } from '../utils/resolveEnv';
const NotificationsContext = createContext();

export const NotificationsProvider = ({ children }) => {
  const { isLoggedIn, userData } = useLogin();
  const { t } = useTranslation("notifications"); // 👈 namespace correcto
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]); // siempre usar la versión más reciente de t

  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const heartbeatRef = useRef(null);
  const reconnectAllowedRef = useRef(true);

  const [status, setStatus] = useState("idle");
  const listeners = useRef(new Set());

  const getWsUrl = () => {
    const hostname = window.location.hostname;
    let base;
    base = getNotificationsWsUrl(hostname);
    return base;
  };

  // 🔧 Normalizador: usa event como clave en i18n
  const buildNotificationPayload = (msg) => {
    const code = msg?.body?.event || msg?.event || "";
    const creationDate = msg?.body?.creationDate;
    const backendText = msg?.body?.message ?? "";

    // Usar traducciones; si no existen, usar fallback
    const title = tRef.current(`${code}.title`, { defaultValue: code || "Notification" });
    const message = tRef.current(`${code}.message`, { defaultValue: backendText || "" });

    return {
      id: msg?.id || "bellNotification",
      code,
      title,
      message,
      createdAt: creationDate,
      raw: msg,
    };
  };

  const connect = useCallback(() => {
    if (!isLoggedIn || !userData?.access || !userData?.uuid) {
      console.warn("⏸ No se cumplen condiciones para conectar WebSocket de notificaciones.");
      return;
    }

    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;
    setStatus("connecting");

    ws.onopen = () => {
      setStatus("open");
      retryRef.current = 0;
      ws.send(JSON.stringify({
        type: "auth",
        project: "knowledge",
        version: "1.7.5",
        session: userData.uuid,
        token: userData.access,
      }));
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg?.id !== "bellNotification") return;

      const payload = buildNotificationPayload(msg);
      if (!payload.code && !payload.message) return;

      listeners.current.forEach((cb) => cb(payload));
    };

    ws.onclose = () => { setStatus("closed"); scheduleReconnect(); };
    ws.onerror = () => { setStatus("error"); try { ws.close(); } catch { } };

    startHeartbeat();
  }, [isLoggedIn, userData]); // 👈 no dependemos de t aquí

  const scheduleReconnect = () => {
    if (!isLoggedIn || !reconnectAllowedRef.current) return;
    const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
    setTimeout(connect, delay);
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping", session: userData?.uuid }));
        } catch { }
      }
    }, 25000);
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
  };

  const onNotification = (cb) => {
    listeners.current.add(cb);
    return () => listeners.current.delete(cb);
  };

  const disconnect = (reason = "manual") => {
    reconnectAllowedRef.current = false;
    stopHeartbeat();
    if (wsRef.current) { try { wsRef.current.close(1000, reason); } catch { } }
    wsRef.current = null;
    setStatus("closed");
  };

  useEffect(() => {
    if (isLoggedIn) {
      reconnectAllowedRef.current = true;
      connect();
    } else {
      disconnect("no-session");
      setStatus("idle");
    }
    return () => disconnect("unmount");
  }, [isLoggedIn, connect]);

  return (
    <NotificationsContext.Provider value={{ status, onNotification, disconnect }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
